import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  saveOfflineCredentials,
  saveCachedSession,
  saveToken,
  tryOfflineLogin,
} from "@/core/hooks/useOfflineSession";
import { APP_VERSION } from "@/version";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    // --- Etape 1 : TOUJOURS tenter le login offline d'abord ---
    const offlineSession = await tryOfflineLogin(email, password);
    if (offlineSession) {
      saveCachedSession(offlineSession.user);
      setLoading(false);
      navigate("/", { replace: true });

      // Arrière-plan : obtenir un token JWT si on est en ligne
      if (typeof navigator !== "undefined" && navigator.onLine) {
        fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data?.token) saveToken(data.token);
          })
          .catch(() => {});
      }

      return;
    }

    // --- Etape 2 : pas de credentials offline → tenter login en ligne ---
    try {
      const loginPromise = fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const response = await Promise.race([
        loginPromise,
        new Promise<never>((_, rej) =>
          setTimeout(() => rej(new Error("timeout")), 5000)
        ),
      ]);

      const data = await response.json();

      if (!response.ok) {
        setLoading(false);
        setError(data.error || "Email ou mot de passe incorrect");
        return;
      }

      // Login online réussi
      if (data.token) saveToken(data.token);
      if (data.user) {
        const userInfo = {
          id: data.user.id ?? "",
          email: data.user.email ?? email,
          name: data.user.name,
          role: data.user.role,
        };
        await saveOfflineCredentials(email, password, userInfo);
        saveCachedSession(userInfo);
      }

      setLoading(false);
      navigate("/", { replace: true });
    } catch {
      setLoading(false);
      setError(
        "Connexion impossible (pas de réseau et aucun login précédent enregistré)"
      );
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <img
            src="/icons/logo-ultramesure.jpg"
            alt="Ultramesure"
            className="mx-auto size-24 rounded-2xl"
          />
          <h1 className="text-2xl font-bold text-foreground">Ultramesure</h1>
          <p className="text-sm text-muted-foreground">
            Connectez-vous pour continuer
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="vous@exemple.fr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button type="submit" className="w-full" loading={loading}>
            Se connecter
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Mot de passe oublié ?
            </Link>
          </div>
        </form>

        {/* Version */}
        <p className="text-center text-xs text-muted-foreground/50">
          V{APP_VERSION}
        </p>
      </div>
    </div>
  );
}
